import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';

import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { addEventRegistrantPath, removeEventRegistrantPath } from '@/data/events';
import { showAlert } from '@/utils/showAlert';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';

/**
 * Sheet for managing EventRegistrantPaths links for an event instance.
 * Lists linked paths (with remove), and an add section (name input +
 * existing-path picker + create-new option).
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} eventInstanceID
 */
const RegistrantPathsSheet = ({ isOpen, onClose, eventInstanceID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: registrantPaths = [], isFetching } = Query.eventInstances.registrantPaths({
        eventInstanceID,
        options: { enabled: !!isOpen && !!eventInstanceID },
    });
    const manualTriggerPathsQuery = Query.paths.forManualTrigger({
        options: { enabled: !!isOpen },
    });

    const [newName, setNewName] = useState('');
    const [pickedPath, setPickedPath] = useState(null);
    const [showAddSection, setShowAddSection] = useState(false);

    // Reset add-section state when the sheet closes so it doesn't reopen stale.
    useEffect(() => {
        if (!isOpen) {
            setShowAddSection(false);
            setNewName('');
            setPickedPath(null);
        }
    }, [isOpen]);

    const invalidatePaths = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrantPaths(eventInstanceID) });
    }, [queryClient, eventInstanceID]);

    const addLinkMutation = useMutation({
        mutationFn: addEventRegistrantPath,
        onSuccess: () => {
            invalidatePaths();
            Toast.show({ type: 'success', text2: 'Automation linked.', position: 'top' });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to link automation.', position: 'top' });
        },
    });

    const removeLinkMutation = useMutation({
        mutationFn: removeEventRegistrantPath,
        onSuccess: () => {
            invalidatePaths();
            Toast.show({ type: 'success', text2: 'Link removed.', position: 'top' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to remove link.', position: 'top' });
        },
    });

    const handleRemove = useCallback((row) => {
        showAlert({
            title: 'Remove Automation Link',
            subtitle: `Remove "${row.name}" from this event? The automation itself is not deleted.`,
            confirmLabel: 'Remove',
            cancelLabel: 'Cancel',
            isDestructive: true,
            onConfirm: () => removeLinkMutation.mutate({ id: row.id }),
            onCancel: () => {},
        });
    }, [removeLinkMutation]);

    const handleAddExisting = useCallback(() => {
        if (!pickedPath?.id || !newName.trim()) return;
        addLinkMutation.mutate({
            eventInstanceID,
            name: newName.trim(),
            pathID: pickedPath.id,
            createdByPersonID: loggedInUser?.id,
        });
        setNewName('');
        setPickedPath(null);
    }, [pickedPath, newName, eventInstanceID, loggedInUser, addLinkMutation]);

    const handleRowPress = useCallback((row) => {
        onClose?.();
        router.push('/automations/' + row.pathID);
    }, [onClose, router]);

    const trimmedName = newName.trim();
    const canSubmit = trimmedName.length > 0 && !addLinkMutation.isPending;

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title="Registrant Automations"
            cancelText="Close"
        >
            <View style={styles.container} testID="registrant-paths-sheet">
                {/* Add a New Registrant Path toggle */}
                <SolidButton
                    size="sm"
                    type="primary"
                    variant="muted"
                    text={showAddSection ? "Cancel" : "New Registrant Automation"}
                    iconName={showAddSection ? "close-outline" : "add-outline"}
                    onPress={() => setShowAddSection(prev => !prev)}
                    testID="registrant-path-add-toggle"
                    width={200}
                />

                {/* Add section (toggled) */}
                {showAddSection && (
                    <View style={styles.addSection}>
                        <Text style={styles.sectionTitle}>Add an Automation</Text>
                        <View style={styles.inputsRow}>
                            <TextInput
                                style={styles.nameInput}
                                placeholder="Display name (button label)"
                                placeholderTextColor="#949eb1"
                                value={newName}
                                onChangeText={setNewName}
                                testID="registrant-path-name-input"
                            />
                            <View style={styles.pathSelectorWrap}>
                                <SheetSelector
                                    type="paths"
                                    placeholder="Pick an automation"
                                    queryResult={manualTriggerPathsQuery}
                                    value={pickedPath ? [pickedPath] : []}
                                    helpText="The automation must first be created in the Automation builder and set to a Manual Run trigger."
                                    onChange={(selected) => {
                                        const path = Array.isArray(selected) ? selected[0] : selected;
                                        setPickedPath(path || null);
                                    }}
                                />
                            </View>
                        </View>
                        <SolidButton
                            size="sm"
                            type="success"
                            text="Confirm"
                            disabled={!canSubmit || !pickedPath?.id}
                            processing={addLinkMutation.isPending}
                            onPress={handleAddExisting}
                            testID="registrant-path-link-existing"
                            width={100}
                        />
                    </View>
                )}

                {/* Description — Run Automation vs Start Follow Up Path stay
                    distinct (§14.5): these links store legacy pathID bindings
                    that start automation runs. */}
                <Text style={styles.description}>
                    Linked automations appear as buttons on a person&apos;s registration. When tapped, the person starts a run in the linked automation. Only automations set to a Manual Run trigger can be linked here. To start a Follow Up path instead, open the registrant and use the Follow Up section.
                </Text>

                {/* List of linked paths */}
                <Text style={styles.sectionTitle}>Linked Automations</Text>
                {isFetching && registrantPaths.length === 0 ? (
                    <View style={styles.loadingRow}>
                        <ActivityIndicator />
                    </View>
                ) : registrantPaths.length === 0 ? (
                    <EmptyListPlaceholder message="No automations linked yet." />
                ) : (
                    <ScrollView style={styles.list} contentContainerStyle={styles.listContent} keyboardShouldPersistTaps="handled">
                        {registrantPaths.map(row => (
                            <View key={row.id} style={styles.linkRow}>
                                <Pressable style={styles.linkMain} onPress={() => handleRowPress(row)}>
                                    <View style={styles.linkInfo}>
                                        <Text style={styles.linkName} numberOfLines={1}>{row.name}</Text>
                                        <View style={styles.linkSubRow}>
                                            <Text style={styles.linkPathName} numberOfLines={1}>{row.Path?.name}</Text>
                                            {row.Path?.isActive === false && (
                                                <View style={styles.inactiveBadge}>
                                                    <Text style={styles.inactiveBadgeText}>Inactive</Text>
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                    <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
                                </Pressable>
                                <SolidButton
                                    size="sm"
                                    type="error"
                                    variant="muted"
                                    text="Remove"
                                    processing={removeLinkMutation.isPending && removeLinkMutation.variables?.id === row.id}
                                    onPress={() => handleRemove(row)}
                                    testID={`registrant-path-remove-${row.id}`}
                                />
                            </View>
                        ))}
                    </ScrollView>
                )}
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        padding: 16,
        gap: 12,
    },
    description: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 19,
    },
    sectionTitle: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
    },
    loadingRow: {
        paddingVertical: 24,
        alignItems: 'center',
    },
    list: {
        maxHeight: 320,
    },
    listContent: {
        gap: 8,
        paddingBottom: 8,
    },
    linkRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
    },
    linkMain: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    linkInfo: {
        flex: 1,
        gap: 2,
    },
    linkName: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    linkSubRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    linkPathName: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        flexShrink: 1,
    },
    inactiveBadge: {
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 8,
        backgroundColor: colors.warningContainer,
    },
    inactiveBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.warning,
        textTransform: 'uppercase',
    },
    addSection: {
        gap: 10,
        paddingTop: 8,
    },
    inputsRow: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'flex-start',
    },
    pathSelectorWrap: {
        flex: 1,
    },
    nameInput: {
        flex: 1,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.text,
    },
}));

export default RegistrantPathsSheet;
